import { useSyncExternalStore } from 'react'
import { safeLocalStorage } from '@/shared/lib'

/** Имя получателя — `MAX_RECIPIENT_NAME_LENGTH` бэкенда: длиннее схема отбивает `400`. */
export const RECIPIENT_NAME_MAX_LENGTH = 30

/**
 * Поздравление — 120 символов по макету. Бэкенд принимает до 200: фронт
 * строже, противоречия нет (PROJECT.md, «Решения, принятые без бэкенда»).
 */
export const MESSAGE_MAX_LENGTH = 120

/** Заказ, созданный по черновику, и тексты, с которыми он создан. */
export type DraftOrder = {
  id: string
  productId: string
  recipientName: string
  message: string
}

/**
 * Черновик чекаута: после ошибки, выхода или перезапуска Mini App человек
 * возвращается к тому же поздравлению, а не к пустой форме (CLAUDE.md,
 * «Правила поллинга»).
 *
 * Черновик один, а не на каждый номинал: «Изменить» в чекауте уводит к сетке
 * номиналов, и поздравление обязано пережить смену суммы.
 */
export type CheckoutDraft = {
  recipientName: string
  message: string
  /**
   * Заказ, уже созданный по этому черновику. Повтор оплаты идёт по нему
   * (`POST /pay`), а не новым заказом — иначе три отказа банка стали бы тремя
   * покупками. Тексты заказа зафиксированы при создании: поменял поздравление —
   * нужен новый заказ.
   */
  order: DraftOrder | null
  savedAt: number
}

const STORAGE_KEY = 'tapgift.checkoutDraft'

/** Неделя: дольше черновик скорее мешает, чем помогает. */
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000

const EMPTY_DRAFT: CheckoutDraft = { recipientName: '', message: '', order: null, savedAt: 0 }

const isString = (value: unknown): value is string => typeof value === 'string'

const parseOrder = (value: unknown): DraftOrder | null => {
  if (typeof value !== 'object' || value === null) {
    return null
  }

  const order = value as Record<string, unknown>

  return isString(order.id) && isString(order.productId) && isString(order.recipientName) && isString(order.message)
    ? { id: order.id, productId: order.productId, recipientName: order.recipientName, message: order.message }
    : null
}

/** Битый или протухший черновик — пустой: форма не должна падать из-за хранилища. */
const read = (): CheckoutDraft => {
  const raw = safeLocalStorage.getItem(STORAGE_KEY)

  if (!raw) {
    return EMPTY_DRAFT
  }

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const savedAt = typeof parsed.savedAt === 'number' ? parsed.savedAt : 0

    if (Date.now() - savedAt > DRAFT_TTL_MS) {
      return EMPTY_DRAFT
    }

    return {
      recipientName: isString(parsed.recipientName) ? parsed.recipientName.slice(0, RECIPIENT_NAME_MAX_LENGTH) : '',
      message: isString(parsed.message) ? parsed.message.slice(0, MESSAGE_MAX_LENGTH) : '',
      order: parseOrder(parsed.order),
      savedAt,
    }
  } catch {
    return EMPTY_DRAFT
  }
}

/** Снимок для `useSyncExternalStore`: один объект, пока черновик не менялся. */
let snapshot: CheckoutDraft | null = null
const listeners = new Set<() => void>()

const getSnapshot = (): CheckoutDraft => {
  snapshot ??= read()

  return snapshot
}

const write = (next: CheckoutDraft | null): void => {
  if (next) {
    safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } else {
    safeLocalStorage.removeItem(STORAGE_KEY)
  }

  snapshot = next ?? EMPTY_DRAFT
  listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)

  return () => listeners.delete(listener)
}

export const getCheckoutDraft = (): CheckoutDraft => getSnapshot()

/** Набранный текст пишется сразу: закрыть Mini App можно в любую секунду. */
export const updateCheckoutDraft = (patch: Partial<Pick<CheckoutDraft, 'recipientName' | 'message'>>): void =>
  write({ ...getSnapshot(), ...patch, savedAt: Date.now() })

/** Заказ создан по текущим текстам черновика — следующая оплата пойдёт по нему. */
export const rememberDraftOrder = (order: { id: string; productId: string }): void => {
  const draft = getSnapshot()

  write({
    ...draft,
    order: { id: order.id, productId: order.productId, recipientName: draft.recipientName, message: draft.message },
    savedAt: Date.now(),
  })
}

/** Подарок оплачен: следующий начинается с пустой формы. */
export const clearCheckoutDraft = (): void => write(null)

/**
 * Заказ черновика, по которому можно платить этот номинал: тот же продукт и
 * те же тексты, с которыми заказ создан. Иначе — `null`, нужен новый заказ.
 */
export const findDraftOrder = (draft: CheckoutDraft, productId: string): DraftOrder | null =>
  draft.order &&
  draft.order.productId === productId &&
  draft.order.recipientName === draft.recipientName &&
  draft.order.message === draft.message
    ? draft.order
    : null

export const useCheckoutDraft = (): CheckoutDraft => useSyncExternalStore(subscribe, getSnapshot)
